import { type AxiosPromise } from "axios";
import { type TableColumn } from "components/Tables";
import { type CardSections } from "components/Tables/CardList";
import { type MySelectionOptions } from "components/Tables/MySelection/MySelectionContext";

export interface ListTemplateInstruction extends server.dto.IPaginationParameters {
  cache?: server.dto.FilterSets
  onlyMySelection?: boolean
}

export interface ListTemplateExtraFilter<TInstruction> {
  instruction: TInstruction
  initialLoading: boolean
}

export interface ListTemplateOptions<TItem, TInstruction> {
  filterPreset?: {
    cacheKey: server.dto.FilterSets
    listApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.FilterPreset<TInstruction>[]>>
    mutateApi: (instruction: server.dto.CreateFilterPresetInstruction<TInstruction>) => AxiosPromise<any>
  }
  mySelection?: MySelectionOptions<TItem>
  exportOptions?: {
    canExportList: boolean
    exportOptionsApi: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>
  }
}

export interface TableLayout<TItem> {
  type: "table"
  hideHeader?: boolean
  columns: Array<TableColumn<TItem>>
};

export interface CardLayout<TItem> {
  type: "card"
  sections: CardSections<TItem>
};

export interface ToggleableLayout<TItem> {
  type: "both"
  toggleCacheKey: string
  columns: Array<TableColumn<TItem>>
  sections: CardSections<TItem>
};

export type ListTemplateLayout<TItem> =
  | TableLayout<TItem>
  | CardLayout<TItem>
  | ToggleableLayout<TItem>;

type ResponseWithRequest<TItem, TFilter> = server.dto.ApiResponseWrapperWithRequest<TItem[], TFilter> | server.dto.ListTemplateApiResponseWrapper<TItem[], TFilter>;

export type AxiosPromiseWithRequest<TItem, TFilter> = AxiosPromise<ResponseWithRequest<TItem, TFilter>>;

export const hasMySelection = <TItem extends server.dto.IHasObjectPermissions, TFilter>
(res: ResponseWithRequest<TItem, TFilter>): res is server.dto.ListTemplateApiResponseWrapper<TItem[], TFilter> => "mySelectionCount" in res;